import React from "react";
import { motion } from "framer-motion";
import {
  CreditCard,
  Building2,
  Trash2,
  AlertTriangle,
  X,
} from "lucide-react";
import { trackPaymentMethodDeleted } from "../../utils/posthogTracking";

const ModalConfirmDeletePayment = (props) => {
  const { paymentMethod, onConfirm, onCancel } = props.modalProps;

  const getIconByType = (type) => {
    switch (type) {
      case "CHECKING":
      case "SAVINGS":
        return Building2;
      default:
        return CreditCard;
    }
  };

  const getCardTypeColor = (type) => {
    switch (type) {
      case "CHECKING":
      case "SAVINGS":
        return "from-green-400 to-emerald-500";
      default:
        return "from-blue-400 to-purple-500";
    }
  };

  const handleConfirm = () => {
    // Track payment method deletion
    const methodType = paymentMethod.method_type === "CHECKING" || paymentMethod.method_type === "SAVINGS"
      ? "bank_account"
      : "credit_card";
    trackPaymentMethodDeleted(methodType);
    
    if (onConfirm) {
      onConfirm(paymentMethod.id);
    }
    if (onCancel) {
      onCancel(); // This will close the modal
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const Icon = getIconByType(paymentMethod.method_type);
  const gradientColor = getCardTypeColor(paymentMethod.method_type);

  // Animation variants
  const contentAnimation = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="flex flex-col h-full max-h-[100vh] sm:max-h-[90vh] p-0">
      {/* Modal Header */}
      <div className="relative bg-gradient-to-r from-red-600 to-red-700 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 flex-shrink-0 sm:rounded-t-2xl">
        <button
          onClick={handleCancel}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group z-10"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
        </button>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3 sm:gap-4 pr-10"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
              Remove Payment Method
            </h2>
            <p className="text-red-100 text-sm sm:text-base">
              Confirm removal of payment method
            </p>
          </div>
        </motion.div>
      </div>

      {/* Modal Body */}
      <motion.div
        className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 min-h-0 overscroll-contain bg-gray-900"
        variants={contentAnimation}
        initial="hidden"
        animate="visible"
      >
        {/* Payment Method Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-6 p-4 bg-white/[0.03] rounded-xl border border-white/10 backdrop-blur-sm"
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-3 bg-gradient-to-br ${gradientColor} bg-opacity-20 rounded-lg`}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-semibold text-white">
                {paymentMethod.method_desc}
              </div>
              <div className="text-sm text-white/60">
                {paymentMethod.method_type === "CHECKING" ||
                paymentMethod.method_type === "SAVINGS"
                  ? "Bank Payment Method"
                  : "Card Payment Method"}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Warning Message */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <p className="text-white/80 mb-4">
            Are you sure you want to remove this payment method? This action
            cannot be undone.
          </p>

          {/* Important Note */}
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-300 font-medium mb-1">
                Important Note
              </p>
              <p className="text-sm text-yellow-200/80">
                Removing this payment method will not affect any scheduled
                payments. Those will continue using the originally selected
                method.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex gap-3"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={handleCancel}
            className="flex-1 py-3 px-6 bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 text-white font-semibold rounded-xl transition-all"
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={handleConfirm}
            className="flex-1 py-3 px-6 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white font-semibold rounded-xl shadow-lg shadow-red-500/25 transition-all"
          >
            Remove Method
          </motion.button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ModalConfirmDeletePayment;